// M6 worker engagement layer — fetch/cache GET /api/engage/me (react-query,
// same retry:false-on-maybe-missing convention TrimPage's existing `jobs`
// query already uses for an endpoint that can legitimately come back empty
// or not-yet-deployed) and POST /api/engage/target, plus CLIENT-SIDE
// personal-best / pace comparison helpers for the done-screen celebration
// and the live "vs your average" pills. The parallel engine slice owns the
// endpoints themselves — this file only knows the frozen response shapes.
//
// Feature-degrade contract: every consumer must tolerate `data === undefined`
// (loading, 404, offline, any fetch failure) by rendering NOTHING rather
// than a skeleton or error — this whole layer is a bonus on top of the core
// trim flow, never a blocking dependency. See HomeDashboard's ring +
// streak/level chips and TrimPage's pace pill / done-screen celebration.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api, readJson } from "./api";

export type EngagePb = {
  trim_type: string;
  nug_size: string;
  best_gpm: number;
  best_adj_gpm: number;
  best_run_grams: number;
  achieved_at: number;
};

export type EngageTarget = { grams: number; progress_pct: number } | { grams: null; progress_pct: null };

export type EngageToday = {
  grams: number;
  runs: number;
  active_min: number;
  avg_gpm: number;
  adj_gpm: number;
  vs_avg_pct: number | null; // null = no trailing-14d history yet (server contract)
};

export type EngageStreak = {
  days: number;
  above_avg_days: number;
  freeze_available: boolean;
  freeze_used_this_week: boolean;
};

export type EngageLevel = {
  lifetime_grams: number;
  title: string;
  next_title: string | null;
  next_at_grams: number | null;
};

// Piece-rate earnings — null until the owner sets a rate in the console,
// and absent entirely from an older API; consumers hide every money surface
// on either (same silent degrade as the rest of this layer).
export type EngageEarnings = {
  rate_cents_per_g: number;
  currency: string;
  today_cents: number;
  lifetime_cents: number;
};

export type EngageMe = {
  player: { id: number; name: string };
  pbs: EngagePb[];
  today: EngageToday;
  target: EngageTarget;
  streak: EngageStreak;
  level: EngageLevel;
  // null when the owner keeps the leaderboard hidden from this viewer.
  percentile: { today_band: string } | null;
  earnings?: EngageEarnings | null;
};

/** "$6.25" / "€6.25" — Intl handles the symbol per currency; falls back to
 * a plain code prefix if the runtime doesn't know the currency. */
export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency, currencyDisplay: "narrowSymbol",
    }).format(cents / 100);
  } catch {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }
}

export const ENGAGE_ME_KEY = ["engage-me"];

// Fetched once per mount like TrimPage's existing `strains`/`jobs` queries —
// react-query's cache then makes this act as a stable "at trim start"
// snapshot for the rest of the page's life (nothing here invalidates it
// mid-run; TrimPage.saveRun() does that AFTER computing the done-screen
// verdict from this same now-stale-but-correct-at-the-time snapshot, same
// as how a personal best is only ever judged against what was true before
// the run that might just have broken it).
export function useEngageMe(): UseQueryResult<EngageMe> {
  return useQuery({
    queryKey: ENGAGE_ME_KEY,
    queryFn: () => api.get("/api/engage/me").then((r) => readJson<EngageMe>(r)),
    retry: false,
  });
}

export async function setEngageTarget(grams: number | null): Promise<EngageTarget> {
  const res = await api.postJson("/api/engage/target", { grams });
  const body = await readJson<{ ok: true; target: EngageTarget }>(res);
  return body.target;
}

// ── Client-side PB / pace comparison ─────────────────────────────────────
// POST /api/runs's response shape is frozen and carries no PB/avg verdict —
// engage/me's cached `pbs` + `today.avg_gpm` (fetched at trim start, BEFORE
// this run) are the only inputs, compared entirely on the client so saving
// never needs a new API shape.

export type FinishedRun = { grams: number | null; active_ms: number; trim_type: string; nug_size: string };

export type PbVerdict = {
  gpm: number | null;
  isPbGpm: boolean;
  isPbGrams: boolean;
  vsAvgPct: number | null;
  priorBestGpm: number | null;
};

// Shared by the done-screen verdict and the live pace pills (TrimPage's
// timer step + ScaleCamOverlay) — a falsy avg (0 or unknown) means "no
// baseline yet" rather than "0% off your average".
export function paceVsAvgPct(liveGpm: number | null | undefined, avgGpm: number | null | undefined): number | null {
  if (liveGpm == null || !avgGpm) return null;
  return Math.round(((liveGpm - avgGpm) / avgGpm) * 100);
}

export function comparePb(
  run: FinishedRun,
  pbs: EngagePb[] | undefined,
  todayAvgGpm: number | undefined,
): PbVerdict {
  const minutes = run.active_ms / 60000;
  const gpm = run.grams != null && minutes > 0 ? run.grams / minutes : null;
  const priorPb = pbs?.find((p) => p.trim_type === run.trim_type && p.nug_size === run.nug_size) ?? null;
  return {
    gpm,
    isPbGpm: gpm != null && (!priorPb || gpm > priorPb.best_gpm),
    isPbGrams: run.grams != null && (!priorPb || run.grams > priorPb.best_run_grams),
    vsAvgPct: paceVsAvgPct(gpm, todayAvgGpm),
    priorBestGpm: priorPb?.best_gpm ?? null,
  };
}

export type CelebrationTier = "pb" | "avg" | "solid";

// self-referenced before rivalrous: highest tier a solo worker can hit
// against their OWN history, never a comparison to anyone else.
export function celebrationTier(v: PbVerdict): CelebrationTier {
  if (v.isPbGpm || v.isPbGrams) return "pb";
  if (v.vsAvgPct != null && v.vsAvgPct > 0) return "avg";
  return "solid";
}
